// Frontend: RecordTable.jsx (no changes needed, as TableRow handles the popup)
import React, { useEffect, useMemo, useState } from 'react';
import Classes from './RecordTable.module.css';
import useScreenWidth from '../../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import TableRow from './TableRow';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import backend from '../../../../../../../api';

function fmtMoney(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}
function fmtDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const STATUS_DB_TO_UI = {
  completed: "Completed",
  upcoming: "Upcoming",
  cancelled: "Cancelled",
  on_the_way: "On the way",
  delayed: "Delayed",
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function RecordTable() {
  const { pathname } = useLocation();
  const isAppointments = pathname.startsWith('/appointments');
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const { user, token } = useSelector((s) => s?.auth || {});
  const userId = user?._id;

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const itemsPerPage = isAppointments ? 8 : 5;

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await backend.get(`/api/bookings/customer?userId=${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const arr = data?.data || [];
      setBookings(arr.map(b => ({
        ...b,
        ui: {
          _id: b._id,
          companyId: b.companyId,
          service: "Mobile Fuel Delivery",
          amount: fmtMoney(b?.pricing?.total || 0),
          bookingDate: fmtDate(b.createdAt),
          serviceDate: `${fmtDate(b?.appointment?.date) || '-'} • ${b?.appointment?.timeSlot || '-'}`,
          status: { label: STATUS_DB_TO_UI[b.status] || b.status },
          raw: b,
          dateObj: b?.appointment?.date ? new Date(b.appointment.date) : null,
        }
      })));
    } catch (e) {
      console.error('fetch bookings error', e);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const totalPages = Math.ceil(bookings.length / itemsPerPage) || 1;
  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return bookings.slice(start, start + itemsPerPage).map(b => b.ui);
  }, [bookings, currentPage, itemsPerPage]);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, bookings.length);
  const totalItems = bookings.length;

  const counts = useMemo(() => {
    const c = { completed: 0, upcoming: 0, cancelled: 0, delayed: 0, on_the_way: 0 };
    for (const b of bookings) {
      c[b.status] = (c[b.status] || 0) + 1;
    }
    return c;
  }, [bookings]);

  function handlePageChange(_, pageNum) {
    setCurrentPage(pageNum);
  }

  // Calendar logic
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, events: [] });
    }

    // Add days with events
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const events = bookings.filter(b => b.ui.dateObj && 
        b.ui.dateObj.getFullYear() === year && 
        b.ui.dateObj.getMonth() === month && 
        b.ui.dateObj.getDate() === day).map(b => b.ui);
      days.push({ day, events });
    }

    // Add empty cells to fill the last week
    while (days.length % 7 !== 0) {
      days.push({ day: null, events: [] });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const maxVisibleEvents = 3;

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>
          <div className={Classes.tableBanner}>
            <div className="flexAlign10">
              {isAppointments ? (
                <>
                  <div className={Classes.tableBannerItem}>
                    <h5>Completed</h5>
                    <h4>{counts.completed}</h4>
                  </div>
                  <div className={Classes.tableBannerItem}>
                    <h5>Upcoming</h5>
                    <h4>{counts.upcoming + counts.on_the_way + counts.delayed}</h4>
                  </div>
                  <div className={Classes.tableBannerItem}>
                    <h5>Cancelled</h5>
                    <h4>{counts.cancelled}</h4>
                  </div>
                  <div className={Classes.tableBannerItem}>
                    <h5 className={Classes.allText}>All</h5>
                    <h4>{counts.completed + counts.upcoming + counts.on_the_way + counts.delayed + counts.cancelled}</h4>
                  </div>
                </>
              ) : (
                <div className={Classes.tableBannerItem}>
                  <h5>Recent Appointments</h5>
                </div>
              )}
              <div className={Classes.viewToggle}>
                <button 
                  className={`${Classes.viewBtn} ${viewMode === 'table' ? Classes.activeView : ''}`}
                  onClick={() => setViewMode('table')}
                >
                  Table
                </button>
                <button 
                  className={`${Classes.viewBtn} ${viewMode === 'calendar' ? Classes.activeView : ''}`}
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </button>
              </div>
            </div>
            <div>
              {isAppointments ? (
                <h5 className={Classes.headerText}>
                  Need help with your appointment? Message us{' '}
                  <span onClick={() => navigateTo('/contact-us')}>here</span>
                </h5>
              ) : (
                <button
                  type="button"
                  className={Classes.viewBtn}
                  onClick={() => navigateTo('/appointments')}
                >
                  View all {'>'}
                </button>
              )}
            </div>
          </div>

          {viewMode === 'table' ? (
            <>
              <div className={Classes.tableHeader}>
                <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Service</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header2}`}><h5>Amount</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Booking Date</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Service Date</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Status</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header6}`}><h5>Track</h5></div>
                <div className={`${Classes.headerItem} ${Classes.header7}`}></div>
              </div>

              <div className={Classes.tableBody}>
                {loading ? (
                  <SkeletonLoading
                    length={itemsPerPage}
                    height={isMobile ? '3.5rem' : '2.8rem'}
                    width="100%"
                    br="6px"
                    padding="0"
                  />
                ) : currentItems && currentItems.length > 0 ? (
                  currentItems.map((b) => (
                    <TableRow
                      key={b._id}
                      value={b}
                      isAppointments={isAppointments}
                      onChanged={load}
                    />
                  ))
                ) : (
                  <h5 className='placeholderText' style={{ margin: '15px 19px' }} >No appointments yet...</h5>
                )}
              </div>

              {currentItems && currentItems.length > 0 && (
                <div className={Classes.pagFooter}>
                  <h5 style={{ height: '1.2rem' }}>
                    {loading ? (
                      <SkeletonLoading height="1.2rem" width="10rem" br="4px" padding="0" />
                    ) : (
                      `Showing ${startItem} to ${endItem} of ${totalItems} results`
                    )}
                  </h5>
                  <Stack spacing={2} className={Classes.pagination}>
                    {loading ? (
                      <SkeletonLoading height="1.2rem" width="5rem" br="4px" padding="0" />
                    ) : (
                      <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />
                    )}
                  </Stack>
                </div>
              )}
            </>
          ) : (
            <div className={Classes.calendar}>
              <div className={Classes.calendarHeader}>
                <button className={Classes.calendarNavBtn} onClick={handlePrevMonth}>←</button>
                <h5>{MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h5>
                <button className={Classes.calendarNavBtn} onClick={handleNextMonth}>→</button>
              </div>
              <div className={Classes.calendarGrid}>
                {DAYS.map((day, idx) => (
                  <div key={idx} className={Classes.calendarDayHeader}>{day}</div>
                ))}
                {calendarDays.map((day, idx) => (
                  <div key={idx} className={`${Classes.calendarDay} ${!day.day ? Classes.emptyDay : ''}`}>
                    {day.day && (
                      <>
                        <span className={Classes.dayNumber}>{day.day}</span>
                        <div className={Classes.eventsContainer}>
                          {day.events.slice(0, maxVisibleEvents).map((event, eventIdx) => (
                            <div
                              key={event._id + eventIdx}
                              className={`${Classes.calendarEvent} ${Classes[event.status.label.toLowerCase().replace(/\s+/g, '_') + 'Event']}`}
                              // onClick={() => navigateTo(`/view-appointment/details/${event._id}`)}
                            >
                              <span>{event.service}</span>
                              <span>{event.status.label}</span>
                            </div>
                          ))}
                          {day.events.length > maxVisibleEvents && (
                            <div 
                              className={Classes.seeMore}
                              onClick={() => setSelectedDay(day)}
                            >
                              See {day.events.length - maxVisibleEvents} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedDay && (
        <div className={Classes.calendarPopup}>
          <div className={Classes.popupContent}>
            <h5>Appointments on {selectedDay.day} {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</h5>
            {selectedDay.events.map((event, eventIdx) => (
              <div
                key={event._id + eventIdx}
                className={`${Classes.calendarEvent} ${Classes[event.status.label.toLowerCase().replace(/\s+/g, '_') + 'Event']}`}
                // onClick={() => navigateTo(`/view-appointment/details/${event._id}`)}
              >
                <span>{event.service}</span>
                <span>{event.status.label}</span>
              </div>
            ))}
            <button className={Classes.closeBtn} onClick={() => setSelectedDay(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}